// Guards DOM contre le crash React « removeChild NotFoundError » (Sentry BIZCOACH-1M).
//
// `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed
// is not a child of this node.` survient quand un TIERS mute le DOM géré par React —
// quasi toujours une extension de traduction du navigateur (Google Translate) qui
// clone/déplace des nœuds texte, parfois le widget Zoho SalesIQ. React tente ensuite
// de retirer/insérer un nœud qui n'est plus enfant de son parent d'origine, et
// `Node.prototype.removeChild`/`insertBefore` lèvent → le rendu casse (écran figé).
//
// Correctif recommandé par la communauté React/Google : rendre ces deux méthodes
// défensives — si le nœud n'est pas (ou plus) enfant du parent visé, on ne lève pas,
// on renvoie le nœud (no-op gracieux). React se resynchronise au render suivant.

// Type minimal d'un nœud DOM manipulé ici (évite la dépendance au lib.dom en test).
interface NodeLike {
  parentNode?: NodeLike | null;
}

// Constructeur minimal « Node-like » : porte un prototype avec removeChild/insertBefore.
interface NodeCtorLike {
  prototype: {
    removeChild?: (this: NodeLike, child: NodeLike) => NodeLike;
    insertBefore?: (this: NodeLike, newNode: NodeLike, referenceNode: NodeLike | null) => NodeLike;
    __bizcoachDomGuarded?: boolean;
  };
}

/**
 * Installe les guards défensifs sur `nodeCtor.prototype` (par défaut `globalThis.Node`).
 * Idempotent (drapeau `__bizcoachDomGuarded`) et no-op si aucun DOM n'est présent
 * (serveur, tests sans jsdom) → sûr à appeler inconditionnellement au démarrage.
 *
 * @returns `true` si les guards ont été (ou sont déjà) posés, `false` si pas de DOM.
 */
export function installDomGuards(
  nodeCtor: NodeCtorLike | undefined = (globalThis as { Node?: NodeCtorLike }).Node,
): boolean {
  if (typeof nodeCtor !== "function" || !nodeCtor.prototype) return false;

  const proto = nodeCtor.prototype;
  if (proto.__bizcoachDomGuarded) return true; // déjà patché (ex. HMR)

  const originalRemoveChild = proto.removeChild;
  if (typeof originalRemoveChild === "function") {
    proto.removeChild = function <T extends NodeLike>(this: NodeLike, child: T): T {
      if (child && child.parentNode !== this) {
        // Le nœud a été déplacé/retiré par un tiers : ne pas lever, React se recale ensuite.
        return child;
      }
      return originalRemoveChild.apply(this, arguments as unknown as [NodeLike]) as T;
    } as typeof proto.removeChild;
  }

  const originalInsertBefore = proto.insertBefore;
  if (typeof originalInsertBefore === "function") {
    proto.insertBefore = function <T extends NodeLike>(
      this: NodeLike,
      newNode: T,
      referenceNode: NodeLike | null,
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        // Le nœud de référence n'appartient plus à ce parent : insertion impossible sans lever.
        return newNode;
      }
      return originalInsertBefore.apply(
        this,
        arguments as unknown as [NodeLike, NodeLike | null],
      ) as T;
    } as typeof proto.insertBefore;
  }

  proto.__bizcoachDomGuarded = true;
  return true;
}
